import React from 'react';

import { FormattedMessage } from 'react-intl-next';
import { di } from 'react-magnetic-di';

import Button from '@atlaskit/button/new';
import Form, { ErrorMessage, FormSection } from '@atlaskit/form';
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle } from '@atlaskit/modal-dialog';

import messages from '../../../common/messages';
import {
	type Ari,
	type SelectedRule,
	type SelectorOption,
	type UserInputPrompt,
	type UserInputs,
	UserInputType,
	type UserInputValue,
} from '../../../common/types';
import { AutomationIntlMessagesProvider, locales } from '../../../common/utils/i18n';

import BooleanInputPrompt from './boolean-prompt/main';
import NumberInputPrompt from './number-prompt/main';
import ParagraphInputPrompt from './paragraph-prompt/main';
import SelectInputPrompt from './select-prompt/main';
import TextInputPrompt from './text-prompt/main';

export interface UserInputProps {
	clearSelectedRule: () => any;
	invokeRule: (ruleId: number, objects: Ari[], userInputs?: UserInputs) => Promise<void>;
	onInputsModalClosed?: () => void;

	onInputsModalOpened?: () => void;
	selectedRule: SelectedRule;
}

export const UserInputForm = (props: UserInputProps): React.JSX.Element => {
	di(
		BooleanInputPrompt,
		Button,
		ErrorMessage,
		Form,
		FormSection,
		Modal,
		ModalBody,
		ModalFooter,
		ModalHeader,
		ModalTitle,
		NumberInputPrompt,
		SelectInputPrompt,
		TextInputPrompt,
	);

	const { selectedRule, clearSelectedRule, invokeRule, onInputsModalOpened, onInputsModalClosed } =
		props;
	const { rule, objects } = selectedRule;
	const { userInputPrompts } = rule;

	const getTypeOfInput = (key: string, prompts: UserInputPrompt[]) =>
		prompts.find(({ variableName }) => variableName === key)?.inputType;

	const generateInputValue = (inputType: UserInputType, data: any): UserInputValue => {
		switch (inputType) {
			case UserInputType.DROPDOWN:
				return {
					inputType,
					value: data ? (data as SelectorOption).value : '',
				};
			case UserInputType.NUMBER:
				return {
					inputType,
					value: Number(data),
				};
			default:
				return {
					inputType,
					value: data,
				};
		}
	};

	const handleSubmit = (data: Record<string, string>) => {
		const output = Object.entries(data).reduce((acc: UserInputs, [key, value]) => {
			const inputType = getTypeOfInput(key, userInputPrompts);

			if (!inputType) {
				return acc;
			}

			acc[key] = generateInputValue(inputType, value);

			return acc;
		}, {});

		return invokeRule(rule.id, objects, output);
	};

	const handleOpen = () => {
		if (onInputsModalOpened) {
			onInputsModalOpened();
		}
	};

	const handleClose = () => {
		clearSelectedRule();
		if (onInputsModalClosed) {
			onInputsModalClosed();
		}
	};

	const generateUserInputForm = (inputPrompt: UserInputPrompt) => {
		switch (inputPrompt.inputType) {
			case UserInputType.TEXT:
				return <TextInputPrompt key={inputPrompt.variableName} userInputPrompt={inputPrompt} />;
			case UserInputType.NUMBER:
				return <NumberInputPrompt key={inputPrompt.variableName} userInputPrompt={inputPrompt} />;
			case UserInputType.DROPDOWN:
				return <SelectInputPrompt key={inputPrompt.variableName} userInputPrompt={inputPrompt} />;
			case UserInputType.BOOLEAN:
				return <BooleanInputPrompt key={inputPrompt.variableName} userInputPrompt={inputPrompt} />;
			case UserInputType.PARAGRAPH:
				return (
					<ParagraphInputPrompt key={inputPrompt.variableName} userInputPrompt={inputPrompt} />
				);
			default:
				return (
					<>
						<br />
						<ErrorMessage>
							<FormattedMessage {...messages.errorInputTypeIsNotSupported} />
						</ErrorMessage>
						<br />
					</>
				);
		}
	};

	return (
		<AutomationIntlMessagesProvider locales={locales}>
			<Modal
				data-testid="automation-platform.ui.manual-rules-container.manual-triggers-form.modal"
				onClose={handleClose}
				onOpenComplete={handleOpen}
			>
				<Form<Record<string, string>> onSubmit={handleSubmit}>
					{({ formProps, submitting }) => (
						<form {...formProps}>
							<ModalHeader hasCloseButton>
								<ModalTitle>{rule.name}</ModalTitle>
							</ModalHeader>
							<ModalBody>
								<FormattedMessage {...messages.inputRequiredForAction} />
								<FormSection>{userInputPrompts.map(generateUserInputForm)}</FormSection>
							</ModalBody>
							<ModalFooter>
								<Button appearance="subtle" onClick={handleClose} isDisabled={submitting}>
									<FormattedMessage {...messages.cancelButtonTitle} />
								</Button>
								<Button appearance="primary" type="submit" isLoading={submitting} autoFocus>
									<FormattedMessage {...messages.continueButtonTitle} />
								</Button>
							</ModalFooter>
						</form>
					)}
				</Form>
			</Modal>
		</AutomationIntlMessagesProvider>
	);
};

export default UserInputForm;
